/**
 * Admin — user administration surface. All calls require admin role;
 * the backend enforces at every endpoint and returns role-aware 403 copy.
 */

import type {
  AdminTokenListParams,
  AdminUserListParams,
  AdminUserListResponse,
  BindingCutoverStatus,
  BulkRevokeResponse,
  BulkRevokeSelectedRequest,
  EnforcementStatus,
  FeatureAreaCatalogItem,
  ScopeGrant,
  ScopeGrantCreateBody,
  ScopeGrantListResponse,
  ScopeGrantUpdateBody,
  SessionTokenActivity,
  SessionTokenAdminListResponse,
  SessionTokenDetail,
  UserAdminItem,
  UserAuditEvent,
  UserReasonBody,
  UserRoleUpdateBody,
} from '@/types/auth'
import { apiClient } from './client'

export const admin = {
  listUsers: async (
    params: AdminUserListParams = {}
  ): Promise<AdminUserListResponse> => {
    const response = await apiClient.get<AdminUserListResponse>(
      '/api/v1/auth/users/',
      { params }
    )
    return response.data
  },

  changeRole: async (
    userId: string,
    body: UserRoleUpdateBody
  ): Promise<UserAdminItem> => {
    const response = await apiClient.patch<UserAdminItem>(
      `/api/v1/auth/users/${userId}/role/`,
      body
    )
    return response.data
  },

  disableUser: async (userId: string, body: UserReasonBody): Promise<void> => {
    await apiClient.post(`/api/v1/auth/users/${userId}/disable/`, body)
  },

  enableUser: async (
    userId: string,
    body: UserReasonBody
  ): Promise<UserAdminItem> => {
    const response = await apiClient.post<UserAdminItem>(
      `/api/v1/auth/users/${userId}/enable/`,
      body
    )
    return response.data
  },

  userAuditEvents: async (
    userId: string,
    limit = 50
  ): Promise<UserAuditEvent[]> => {
    const response = await apiClient.get<UserAuditEvent[]>(
      `/api/v1/auth/users/${userId}/audit-events/`,
      { params: { limit } }
    )
    return response.data
  },

  enforcementStatus: async (): Promise<EnforcementStatus> => {
    const response = await apiClient.get<EnforcementStatus>(
      '/api/v1/auth/enforcement-status/'
    )
    return response.data
  },

  // Session tokens — GET / is the human-operator+ admin roster; owner_entra_oid
  // narrows it to one principal for UserDetailForm's live-tokens preview.
  listSessionTokens: async (
    params: AdminTokenListParams
  ): Promise<SessionTokenAdminListResponse> => {
    const response = await apiClient.get<SessionTokenAdminListResponse>(
      '/api/v1/auth/session-tokens/',
      { params }
    )
    return response.data
  },

  getSessionToken: async (id: string): Promise<SessionTokenDetail> => {
    const response = await apiClient.get<SessionTokenDetail>(
      `/api/v1/auth/session-tokens/${id}/`
    )
    return response.data
  },

  getSessionTokenActivity: async (
    id: string
  ): Promise<SessionTokenActivity> => {
    const response = await apiClient.get<SessionTokenActivity>(
      `/api/v1/auth/session-tokens/${id}/activity/`
    )
    return response.data
  },

  // reason travels as a query param, never a DELETE body — some intermediaries
  // silently strip DELETE bodies, which would drop the audit reason without
  // surfacing an error.
  revokeSessionToken: async (
    tokenId: string,
    reason?: string
  ): Promise<void> => {
    await apiClient.delete(`/api/v1/auth/session-tokens/${tokenId}/`, {
      params: { reason },
    })
  },

  bulkRevokeSelected: async (
    tokenIds: string[],
    reason?: string
  ): Promise<BulkRevokeResponse> => {
    const body: BulkRevokeSelectedRequest = { token_ids: tokenIds, reason }
    const response = await apiClient.post<BulkRevokeResponse>(
      '/api/v1/auth/session-tokens/revoke-selected/',
      body
    )
    return response.data
  },

  getBindingCutover: async (): Promise<BindingCutoverStatus> => {
    const response = await apiClient.get<BindingCutoverStatus>(
      '/api/v1/auth/session-tokens/binding-cutover/'
    )
    return response.data
  },

  bulkRevokeSessionTokens: async (
    entraOid?: string
  ): Promise<BulkRevokeResponse> => {
    const response = await apiClient.post<BulkRevokeResponse>(
      '/api/v1/auth/session-tokens/bulk-revoke/',
      entraOid ? { entra_oid: entraOid } : {}
    )
    return response.data
  },

  // ── Scoped grants (PRD-0055) ──────────────────────────────────────────

  listUserGrants: async (userId: string): Promise<ScopeGrantListResponse> => {
    const response = await apiClient.get<ScopeGrantListResponse>(
      `/api/v1/auth/users/${userId}/grants/`
    )
    return response.data
  },

  createGrant: async (
    userId: string,
    body: ScopeGrantCreateBody
  ): Promise<ScopeGrant> => {
    const response = await apiClient.post<ScopeGrant>(
      `/api/v1/auth/users/${userId}/grants/`,
      body
    )
    return response.data
  },

  updateGrant: async (
    userId: string,
    grantId: string,
    body: ScopeGrantUpdateBody
  ): Promise<ScopeGrant> => {
    const response = await apiClient.patch<ScopeGrant>(
      `/api/v1/auth/users/${userId}/grants/${grantId}/`,
      body
    )
    return response.data
  },

  revokeGrant: async (userId: string, grantId: string): Promise<void> => {
    await apiClient.delete(
      `/api/v1/auth/users/${userId}/grants/${grantId}/`
    )
  },

  listFeatureAreas: async (): Promise<FeatureAreaCatalogItem[]> => {
    const response = await apiClient.get<FeatureAreaCatalogItem[]>(
      '/api/v1/auth/feature-areas/'
    )
    return response.data
  },
}
